import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Box, Button, CircularProgress, Typography, Alert, TextField } from '@mui/material';

const cardStyle = {
  style: {
    base: {
      color: '#434343',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      '::placeholder': {
        color: '#a0a0a0',
      },
    },
    invalid: {
      color: '#ff5252',
      iconColor: '#ff5252',
    },
  },
  hidePostalCode: true
};

/**
 * PaymentForm component for handling credit card payments with Stripe Elements
 */
const PaymentForm = ({ clientSecret, packageDetails, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [nameError, setNameError] = useState('');
  const [securityWarning, setSecurityWarning] = useState(null);

  // Check if the site is running on HTTPS (on initial render only)
  useEffect(() => {
    // Only check in production
    if (process.env.REACT_APP_NODE_ENV === 'production' && window.location.protocol !== 'https:') {
      console.error('Payment form loaded over non-secure connection!');
      setSecurityWarning('Warning: This page is not being served over a secure connection (HTTPS). Do not enter real payment information.');
    }
    
    // Clean up sensitive data if component is unmounted
    return () => {
      // Clear any sensitive data in component state when unmounting
      setCustomerName('');
    };
  }, []);

  const handleNameChange = (e) => {
    setCustomerName(e.target.value);
    if (e.target.value.trim() === '') {
      setNameError('Name is required');
    } else {
      setNameError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    // Validate name
    if (customerName.trim() === '') {
      setNameError('Name is required');
      return;
    }

    setProcessing(true);

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      
      // Confirm the card payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: customerName.trim(),
          },
        },
        receipt_email: packageDetails.userEmail,
      });

      if (result.error) {
        // Only call the error callback, don't set local error state
        if (onPaymentError) {
          onPaymentError(result.error);
        }
      } else if (result.paymentIntent.status === 'succeeded') {
        setSucceeded(true);
        
        // Call the success callback with the payment intent ID
        if (onPaymentSuccess) {
          onPaymentSuccess({
            paymentIntentId: result.paymentIntent.id,
            packageDetails
          });
        }
      }
    } catch (err) {
      // Only call the error callback, don't set local error state
      if (onPaymentError) {
        onPaymentError(err);
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {securityWarning && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {securityWarning}
        </Alert>
      )}
      
      {succeeded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Payment successful! Your tokens will be added to your account.
        </Alert>
      )}
      
      <TextField
        label="Name as it appears on Card"
        variant="outlined"
        fullWidth
        value={customerName}
        onChange={handleNameChange}
        error={!!nameError}
        helperText={nameError}
        disabled={processing || succeeded}
        sx={{ mb: 3 }}
        placeholder="Enter the name as it appears on your card"
        inputProps={{ 
          maxLength: 100,
          autoComplete: 'cc-name'
        }}
      />
      
      <Box sx={{ 
        p: 2, 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        mb: 3 
      }}>
        <CardElement options={cardStyle} />
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
        Your payment information is processed securely by Stripe. We don't store your card details.
      </Typography>
      
      <Button
        disabled={processing || succeeded || !stripe || !!securityWarning}
        type="submit"
        variant="contained"
        fullWidth
        sx={{ py: 1.2 }}
      >
        {processing ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Processing...
          </>
        ) : (
          `Pay $${packageDetails.price}`
        )}
      </Button>
    </Box>
  );
};

export default PaymentForm; 